import _map from 'lodash/map';
import _omitBy from 'lodash/omitBy';
import _isNil from 'lodash/isNil';
import _get from 'lodash/get';
import _some from 'lodash/some';
import _find from 'lodash/find';
import _trim from 'lodash/trim';
import _isEmpty from 'lodash/isEmpty';
import _compact from 'lodash/compact';
import _includes from 'lodash/includes';
import { formatISO } from 'date-fns';
import { STRINGS } from 'config';
import { apiPost } from 'common/apiRequests';
import { stringifyDate } from 'common/dates';
import { validateHeightFormat, validateWeightFormat } from 'common/inputs';
import { VALIDATION_MESSAGES } from 'common/validationMessages';
import { sendAnswerToGoogleAnalytics } from 'common/googleAnalytics';
import { setSessionTimeout, restartSession, handleSessionExpiry } from '../session';
import { returnToWebAppContext } from '../webapp';
import { setIsETriageBackHidden } from '../flags';

const { DATE, SINGLE, COMPOUND } = STRINGS.QUESTION_TYPE;

const addBlankToHistory = questionIndex => {
  if (window.history.state == null || window.history.state.questionIndex == null || !(window.history.state.questionIndex >= questionIndex + 1)) {
    window.history.pushState({ questionIndex: questionIndex + 1 }, '', window.location.href);
  }
};

const recordConsultationStartTime = () => ({
  type: STRINGS.CONSULTATION_REDUCER.SET_START_TIME,
  payload: new Date(),
});

export const setPaused = paused => ({
  type: STRINGS.CONSULTATION_REDUCER.SET_PAUSED,
  payload: paused,
});

export const handleETriageBackNavOffTag = question => (dispatch, getState) => {
  const { product } = getState().consultationReducer;
  if (product === STRINGS.PRODUCT.E_TRIAGE && question) {
    if (question.tags && _includes(question.tags, STRINGS.TAGS.QUESTION.BACK_NAV_OFF)) {
      dispatch(setIsETriageBackHidden(true));
    } else {
      dispatch(setIsETriageBackHidden(false));
    }
  }
};

const getAnswerIds = (answers, question) => {
  const singleSelectAnswerId = _get(answers, 'answerId', null);
  const multipleAnswerIds = _compact(_map(answers, 'answerId'));
  const defaultAnswerId = _get(question, 'answers[0].answerId', '');
  if (singleSelectAnswerId) {
    return [singleSelectAnswerId];
  }
  if (!_isEmpty(multipleAnswerIds)) {
    return multipleAnswerIds;
  }
  return [defaultAnswerId];
};

const handleSendAnswersToGoogleAnalytics = (answers, question, treatmentCategory, responseType) => {
  const answerIds = getAnswerIds(answers, question);
  return _map(answerIds, answerId => sendAnswerToGoogleAnalytics(treatmentCategory, answerId, responseType));
};

export const getLastQuestion = () => (dispatch, getState) => {
  const consultationState = getState().consultationReducer;
  const flagsState = getState().flagsReducer;
  const { isClinicalReviewMode } = flagsState;
  const { sessionId, practice, isDisclaimerQuestion } = consultationState;
  if (!sessionId) {
    if (isDisclaimerQuestion) {
      dispatch(restartSession());
    }
    return null;
  }
  dispatch({ type: STRINGS.CONSULTATION_REDUCER.GET_LAST_QUESTION_REQUEST });
  return apiPost('/question', practice.practiceId)
    .type('application/json')
    .send({ sessionId })
    .retry(3, error => {
      if (error) {
        dispatch({ type: `${STRINGS.CONSULTATION_REDUCER.GET_LAST_QUESTION_REQUEST}_RETRY` });
      }
    })
    .then(response => {
      // Restarts the eTriage inactivity timer in the webapp.
      if (window.cancelTimer && window.restartTimer) {
        window.cancelTimer();
        window.restartTimer();
      }
      dispatch({ type: STRINGS.CONSULTATION_REDUCER.GET_LAST_QUESTION_SUCCESS, payload: { response: response.body, isClinicalReviewMode: isClinicalReviewMode } });
      if (response.body) {
        dispatch(handleETriageBackNavOffTag(response.body.question));
      }
    })
    .catch(error => {
      if (error.response && error.response.body && error.response.body.errorCode === STRINGS.SERVICE_ERROR_CODE.SESSION_EXPIRED) {
        dispatch(handleSessionExpiry());
      } else {
        dispatch({ type: STRINGS.CONSULTATION_REDUCER.GET_LAST_QUESTION_ERROR, payload: error });
      }
    });
};

function findMatchingQuestion(subQuestionId, questions) {
  const item = _find(questions, { questionId: subQuestionId });
  return item || null;
}

function findMatchingSubQuestion(subQuestionId, questions) {
  const compoundQuestions = _find(questions, { type: COMPOUND });
  const matchingSubQuestion = findMatchingQuestion(subQuestionId, compoundQuestions.questions);
  return matchingSubQuestion || null;
}

const backToHomePage = () => {
  window.location.href = '/';
};

export const logMessage = (practiceId, sessionId, logLevel, message) => dispatch => {
  const requestBody = {
    practiceId: practiceId,
    sessionId: sessionId,
    logLevel: logLevel,
    message: message,
  };
  return apiPost('/logmessage', practiceId)
    .type('application/json')
    .send(requestBody)
    .then({})
    .finally(() => dispatch({ type: 'consultation/LOGMESSAGE_SENT' }));
};

export const getQuestion = (action, consultationState = {}, navigationAction = 'NEXT') => (dispatch, getState) => {
  dispatch({ type: `consultation/${action}_REQUEST` });
  const flagsState = getState().flagsReducer;
  const { isClinicalReviewMode } = flagsState;
  let answers;
  let breakpoint = false;
  if (!consultationState || consultationState.syncRequest || !consultationState.question || !consultationState.question.type || navigationAction === 'PREVIOUS') {
    answers = null;
  } else {
    if (_includes(consultationState.question.tags, STRINGS.TAGS.QUESTION.BREAKPOINT)) {
      breakpoint = true;
    }
    switch (consultationState.question.type) {
      case STRINGS.QUESTION_TYPE.MULTIPLE:
        answers = consultationState.multiSelectedAnswers;
        break;
      case STRINGS.QUESTION_TYPE.ALLMULTIPLE:
        answers = consultationState.multiSelectedAnswers;
        break;
      case STRINGS.QUESTION_TYPE.SINGLE:
        answers = consultationState.singleSelectedAnswer;
        break;
      case STRINGS.QUESTION_TYPE.DATE: {
        const { d, m, y } = consultationState.dateAnswer;
        if (d && m && y) {
          const stringifiedDate = stringifyDate(consultationState.dateAnswer);
          if (!stringifiedDate) {
            return dispatch({
              type: STRINGS.CONSULTATION_REDUCER.GET_NEXT_QUESTION_INVALID_REQUEST_ERROR,
              payload: VALIDATION_MESSAGES.DATE.INVALID,
            });
          }
          answers = [{ valueDate: stringifiedDate }];
        } else {
          if (!consultationState.question.optional && navigationAction === 'NEXT') {
            return dispatch({
              type: STRINGS.CONSULTATION_REDUCER.GET_NEXT_QUESTION_INVALID_REQUEST_ERROR,
              payload: VALIDATION_MESSAGES.DATE.INVALID,
            });
          }
          answers = [{ valueDate: null }];
        }
      }
        break;
      case STRINGS.QUESTION_TYPE.COMPOUND: {
        let criticalError;
        answers = _map(_omitBy(consultationState.compoundAnswers, answer => !answer.answerId), (answer, subQuestionId) => {
          const matchingQuestion = findMatchingQuestion(subQuestionId, consultationState?.question?.questions);
          if (matchingQuestion === null) {
            // compound questions - example address
            const matchingSubQuestion = findMatchingSubQuestion(subQuestionId, consultationState?.question?.questions);
            return {
              type: matchingSubQuestion?.type,
              section: matchingSubQuestion?.section,
              questionText: matchingSubQuestion?.text,
              triageLevel: matchingSubQuestion?.triageLevel,
              ...answer,
            };
          }
          if (matchingQuestion.type === DATE) {
            const stringifiedDate = stringifyDate(answer.value);
            if (!stringifiedDate) {
              // prevent submission of compound question by setting critical validation error
              criticalError = {
                type: STRINGS.CONSULTATION_REDUCER.GET_NEXT_QUESTION_COMPOUND_QUESTION_ERROR,
                payload: {
                  reference: answer.answerId,
                  message: VALIDATION_MESSAGES.DATE.INVALID,
                },
              };
            }
            return {
              answerId: answer.answerId,
              questionTags: answer.questionTags,
              type: matchingQuestion.type,
              section: matchingQuestion.section,
              questionText: matchingQuestion.text,
              triageLevel: matchingQuestion?.triageLevel,
              valueDate: stringifiedDate,
            };
          }
          if (matchingQuestion.type === SINGLE) {
            return {
              answerId: answer.answerId,
              questionTags: answer.questionTags,
              type: matchingQuestion.type,
              section: matchingQuestion.section,
              questionText: matchingQuestion.text,
              triageLevel: matchingQuestion?.triageLevel,
              ...answer.value,
            };
          }
          return {
            type: matchingQuestion?.type,
            section: matchingQuestion?.section,
            triageLevel: matchingQuestion?.triageLevel,
            questionText: matchingQuestion?.text,
            ...answer,
          };
        });
        if (criticalError) {
          return dispatch(criticalError);
        }
        break;
      }
      case STRINGS.QUESTION_TYPE.BLOODPRESSURE:
        answers = [{
          jsonValue: {
            bloodPressure: _map(consultationState.bloodPressureAnswer.bloodPressure, ({ systolic, diastolic, date, time }) => {
              const dateAsDate = new Date(date);
              const timeAsDate = new Date(time);
              const dateTime = date && time ? (
                formatISO(new Date(dateAsDate.getFullYear(), dateAsDate.getMonth(), dateAsDate.getDate(), timeAsDate.getHours(), timeAsDate.getMinutes(), timeAsDate.getSeconds()))
              ) : '';
              return ({
                systolic,
                diastolic,
                date: dateTime,
              });
            }),
          },
        }];
        break;
      case STRINGS.QUESTION_TYPE.BODYMASSINDEX: {
        const { isWeightImperial, isHeightImperial } = consultationState.bmi;
        answers = [{
          jsonValue: {
            weightKilograms: isWeightImperial ? null : consultationState.bmi.weight.kilograms,
            weightPounds: isWeightImperial ? consultationState.bmi.weight.pounds : null,
            weightStone: isWeightImperial ? consultationState.bmi.weight.stone : null,
            heightCentimetres: isHeightImperial ? null : consultationState.bmi.height.centimetres,
            heightFeet: isHeightImperial ? consultationState.bmi.height.feet : null,
            heightInches: isHeightImperial ? consultationState.bmi.height.inches : null,
          },
        }];
      }
        break;
      case STRINGS.QUESTION_TYPE.PHOTO:
        if (_includes(consultationState.question.tags, STRINGS.TAGS.QUESTION.S3_IMAGE_SOLUTION)) {
          answers = [{
            jsonValue: {
              photos: _map(consultationState.photos[consultationState.question.questionId], photo => {
                return { fileName: photo.fileName, fileDescription: _trim(photo.fileDescription) };
              }),
            },
          }];
          break;
        }
        answers = [{
          jsonValue: {
            photos: _map(consultationState.photos[consultationState.question.questionId], photo => {
              const fileDescription = _trim(photo.fileDescription);
              return _omitBy({ ...photo, fileDescription, fileBytes: photo.fileKey ? null : photo.fileBytes }, _isNil);
            }),
          },
        }];
        break;
      case STRINGS.QUESTION_TYPE.LOGIN:
        answers = consultationState.isLoggedIn ? [{ jsonValue: consultationState.loginStatus }] : [];
        break;
      case STRINGS.QUESTION_TYPE.HEIGHT: {
        const isHeightValidFormat = validateHeightFormat(consultationState.freeTextAnswer);
        if (!isHeightValidFormat) {
          return dispatch({
            type: STRINGS.CONSULTATION_REDUCER.GET_NEXT_QUESTION_INVALID_REQUEST_ERROR,
            payload: VALIDATION_MESSAGES.HEIGHT.INVALID_FORMAT,
          });
        }
        answers = [{ value: consultationState.freeTextAnswer }];
      }
        break;
      case STRINGS.QUESTION_TYPE.WEIGHT: {
        const isWeightValidFormat = validateWeightFormat(consultationState.freeTextAnswer);
        if (!isWeightValidFormat) {
          return dispatch({
            type: STRINGS.CONSULTATION_REDUCER.GET_NEXT_QUESTION_INVALID_REQUEST_ERROR,
            payload: VALIDATION_MESSAGES.WEIGHT.INVALID_FORMAT,
          });
        }
        answers = [{ value: consultationState.freeTextAnswer }];
      }
        break;
      case STRINGS.QUESTION_TYPE.SEARCH:
        if (!consultationState.selectedSearchResult) {
          return dispatch({
            type: STRINGS.CONSULTATION_REDUCER.GET_NEXT_QUESTION_INVALID_REQUEST_ERROR,
            payload: VALIDATION_MESSAGES.SEARCH.MISSING_VALUE,
          });
        }
        answers = [{ jsonValue: consultationState.selectedSearchResult }];
        break;
      default:
        answers = [{ value: consultationState.freeTextAnswer }];
        break;
    }
    handleSendAnswersToGoogleAnalytics(answers, consultationState.question, consultationState.treatmentCategory, consultationState.responseType);
  }
  const requestBody = _omitBy(
    {
      conditionId: consultationState.treatmentCategory,
      navigationAction: navigationAction,
      sessionId: consultationState.sessionId ? consultationState.sessionId : null,
      questionId: _get(consultationState, 'question.questionId', null),
      answers: navigationAction === 'NEXT' ? answers : null,
      ...(action === 'GET_FIRST_QUESTION' ? (
        {
          patient: consultationState.patient,
          practice: { practiceId: consultationState.practice.practiceId },
          encounterId: consultationState.encounterId ? consultationState.encounterId : null,
        }
      ) : {}),
      ...(action === 'GET_FIRST_PRELIM_QUESTION' ? (
        {
          practice: { practiceId: consultationState.practice.practiceId },
        }
      ) : {}),
    },
    _isNil,
  );
  return apiPost('/question', consultationState.practice.practiceId)
    .type('application/json')
    .send(requestBody)
    .retry(3, error => {
      if (error) {
        dispatch({ type: `consultation/${action}_REQUEST_RETRY` });
      }
    })
    .then(response => {
      if (!response.body || response.body.errorCode || response.body.errorMessage) {
        dispatch({
          type: STRINGS.CONSULTATION_REDUCER.CONSULTATION_ERROR(action),
          payload: response.body || `${STRINGS.CONSULTATION_REDUCER.CONSULTATION_ERROR(action)}: No response body`,
        });
      } else if (_some(response.body.validationErrors, error => error.code === STRINGS.VALIDATION_ERROR_CODES.INVALID_QUESTION_ID)) {
        dispatch(getLastQuestion());
      } else {
        dispatch(setSessionTimeout());
        if (action === 'GET_PREVIOUS_QUESTION' && consultationState.paused !== true) {
          // when navigating backwards, we need to return to webflow if the response question has a breakpoint
          // or we are between the prelim and clinical Qs
          const prevQ = response.body.question;
          if (!prevQ || _includes(prevQ.tags, STRINGS.TAGS.QUESTION.BREAKPOINT)) {
            // no question in response indicates we have navigated back from the clinical section to the prelim end state
            // call success reducer with empty payload to reset all answer fields
            dispatch({ type: STRINGS.CONSULTATION_REDUCER.GET_PREVIOUS_QUESTION_SUCCESS, payload: { response: {} } });
            breakpoint = true;
          }
        }
        if (!breakpoint) {
          // if we are at a breakpoint don't save the response to state as this causes the question to be shown briefly in the ui
          dispatch({ type: STRINGS.CONSULTATION_REDUCER.CONSULTATION_SUCCESS(action), payload: { response: response.body, isClinicalReviewMode: isClinicalReviewMode } });
        }
        if (action === 'GET_FIRST_QUESTION' || action === 'GET_NEXT_QUESTION') {
          // this may be able to be removed now
          addBlankToHistory(consultationState.questionIndex);
        }
        if (action === 'GET_FIRST_QUESTION') {
          dispatch(recordConsultationStartTime());
        }
        dispatch(handleETriageBackNavOffTag(response.body.question));

        if (consultationState.product === STRINGS.PRODUCT.E_TRIAGE) {
          if (breakpoint || response.body.question.type === 'ENDSTATE') {
            // we have hit a breakpoint or endstate so set consultation to paused & return to webapp context
            dispatch({ type: STRINGS.CONSULTATION_REDUCER.RETURN_TO_WEBFLOW });
            dispatch(setPaused(true));
            dispatch(returnToWebAppContext(action === 'GET_PREVIOUS_QUESTION'));
          }
        }
      }
    })
    .catch(error => {
      const { errorCode } = error.response?.body || {};
      if (errorCode === STRINGS.SERVICE_ERROR_CODE.SESSION_EXPIRED) {
        dispatch(handleSessionExpiry());
      } else if (errorCode === STRINGS.SERVICE_ERROR_CODE.OUT_OF_HOURS) {
        dispatch({ type: STRINGS.CONSULTATION_REDUCER.OUT_OF_HOURS_ERROR, payload: error });
      } else if (errorCode === STRINGS.SERVICE_ERROR_CODE.CONTENT_TYPE_UNAVAILABLE && consultationState.product === STRINGS.PRODUCT.PRIMARY_CARE && action === 'GET_FIRST_QUESTION') {
        backToHomePage();
      } else {
        dispatch({ type: STRINGS.CONSULTATION_REDUCER.CONSULTATION_ERROR(action), payload: error });
      }
    })
    .finally(() => dispatch({ type: 'session/CLEAR_SESSION_VALIDATION' }));
};
